/**
 * materialHierarchy.js \u2014 JS port of the system's canonical raw-material
 * ordering so the printed Jewellery Tag lists materials in the SAME order as
 * the BOM / Raw Material Used table.
 *
 * Mirrors:
 *   - backend MATERIAL_ORDER  : gold \u2192 diamond \u2192 color_stone \u2192 pearl
 *   - core/diamond_hierarchy.py hierarchy_rank(): within Diamonds the
 *     sub-categories ALWAYS render Polki \u2192 Diamonds \u2192 Rose Cuts \u2192 Brown \u2192
 *     Chakri \u2192 Other, regardless of how the codes sort alphabetically.
 *
 * Keep in sync with core/diamond_hierarchy.py.
 */

export const MATERIAL_RANK = { gold: 0, diamond: 1, color_stone: 2, pearl: 3 };

// (prefixes, label) \u2014 longer spellings first so POLKI matches before POL.
const DIAMOND_SUBCATEGORIES = [
  [['POLKI', 'POL'], 'Polki'],
  [['DIAMONDS', 'DIAMOND', 'DIA'], 'Diamonds'],
  [['ROSECUTS', 'ROSECUT', 'ROSE', 'RC'], 'Rose Cuts'],
  [['BROWN', 'BD'], 'Brown Diamonds'],
  [['CHAKRI', 'CHK'], 'Chakri'],
];
const OTHER_RANK = DIAMOND_SUBCATEGORIES.length; // always last

export function diamondHierarchyRank(code) {
  if (!code) return OTHER_RANK;
  const up = String(code).trim().toUpperCase();
  for (let idx = 0; idx < DIAMOND_SUBCATEGORIES.length; idx += 1) {
    const prefixes = DIAMOND_SUBCATEGORIES[idx][0];
    for (const prefix of prefixes) {
      if (up === prefix || up.startsWith(`${prefix}-`)) return idx;
      if (up.startsWith(prefix)) {
        const tail = up.length > prefix.length ? up[prefix.length] : '';
        if (!/[A-Z]/.test(tail)) return idx;
      }
    }
  }
  return OTHER_RANK;
}

/**
 * Stable sort of normalised tag materials ({ materialType, classification })
 * by: material-type rank, then diamond sub-category rank, then classification
 * name (so same-classification entries group together, matching the BOM).
 */
export function sortByMaterialHierarchy(materials) {
  return [...(materials || [])]
    .map((m, i) => ({ m, i }))
    .sort((a, b) => {
      const ra = MATERIAL_RANK[a.m.materialType] ?? 99;
      const rb = MATERIAL_RANK[b.m.materialType] ?? 99;
      if (ra !== rb) return ra - rb;
      const da = diamondHierarchyRank(a.m.classification);
      const db = diamondHierarchyRank(b.m.classification);
      if (da !== db) return da - db;
      const ca = (a.m.classification || '').toLowerCase();
      const cb = (b.m.classification || '').toLowerCase();
      if (ca < cb) return -1;
      if (ca > cb) return 1;
      return a.i - b.i; // stable
    })
    .map((x) => x.m);
}

// Strict tag order: Diamonds (POLKI → DIA → other) → Colour stones → Pearls → others; stable otherwise.
const STRICT_GROUP = { diamond: 0, color_stone: 1, pearl: 2 };
const strictDia = (m) => Math.min(diamondHierarchyRank(m.code || m.rawName || m.classification), 2);
export function strictTagOrder(materials) {
  return (materials || [])
    .map((m, i) => ({ m, i, g: STRICT_GROUP[m.materialType] ?? 3 }))
    .sort((a, b) => a.g - b.g || (a.g === 0 ? strictDia(a.m) - strictDia(b.m) : 0) || a.i - b.i)
    .map((x) => x.m);
}
